import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Layout, ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import { api, resolveImage } from "../lib/api";

const PLACEHOLDER = "https://images.unsplash.com/photo-1449752272861-e37e5cb14b83?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

const STATUS_LABEL = {
  upcoming: "Upcoming",
  under_planning: "Under Planning",
  approved: "Approved",
  in_development: "In Development",
};

export default function MasterPlanCarousel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const scrollerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/master-plans");
        const list = Array.isArray(data.items) ? data.items : [];
        if (!cancelled) setItems(list);
      } catch (e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (loading || items.length === 0) return null;

  return (
    <section className="relative py-14 bg-gradient-to-b from-white via-amber-50/30 to-white z-0" data-testid="master-plan-carousel">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-end justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-amber-800 bg-amber-100 px-2.5 py-1 rounded-full mb-2 whitespace-nowrap">
              <Layout className="h-3 w-3" /> City Master Plans
            </div>
            <h2 className="font-display font-bold text-2xl sm:text-3xl text-neutral-900">Master Plan</h2>
            <p className="text-sm text-muted-foreground mt-1">Officially approved sector, road &amp; land-use master plans for Pataudi and NCR — plan smarter, invest earlier.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => nudge(-1)} className="h-10 w-10 rounded-full border border-border bg-white hover:border-primary/40 flex items-center justify-center transition-colors" aria-label="Previous" data-testid="mp-carousel-prev">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button onClick={() => nudge(1)} className="h-10 w-10 rounded-full border border-border bg-white hover:border-primary/40 flex items-center justify-center transition-colors" aria-label="Next" data-testid="mp-carousel-next">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={scrollerRef}
          className="flex gap-4 overflow-x-auto scroll-smooth no-scrollbar snap-x snap-mandatory"
          style={{ scrollbarWidth: "none" }}
        >
          {items.map((p) => {
            const img = p.image_url ? resolveImage(p.image_url) : PLACEHOLDER;
            const statusLabel = STATUS_LABEL[p.status] || p.status || "";
            return (
              <Link
                key={p.id}
                to={`/master-plan/${p.id}`}
                className="group snap-start shrink-0 w-[280px] sm:w-[340px] bg-white rounded-2xl border border-neutral-200 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_rgba(0,0,0,0.10)] hover:border-neutral-900/20"
                data-testid={`master-plan-card-${p.id}`}
              >
                <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
                  <img src={img} alt={p.title} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[600ms] ease-out" loading="lazy" onError={(e) => { e.currentTarget.src = PLACEHOLDER; }} />
                  <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/45 to-transparent pointer-events-none" />
                  {p.developer_name && (
                    <span className="absolute top-3 left-3 bg-white/95 backdrop-blur text-neutral-900 text-[11px] font-semibold px-2.5 py-1 rounded-full shadow-sm max-w-[65%] truncate">
                      {p.developer_name}
                    </span>
                  )}
                  {statusLabel && (
                    <span className="absolute top-3 right-3 bg-amber-600 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shadow-sm whitespace-nowrap">
                      {statusLabel}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-display font-semibold text-[15px] leading-snug text-neutral-900 line-clamp-1 group-hover:text-primary transition-colors">
                    {p.title}
                  </h3>
                  <p className="flex items-center gap-1 text-neutral-500 text-xs mt-1.5">
                    <MapPin className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{p.location}</span>
                  </p>
                  {p.timeline && (
                    <p className="text-xs font-semibold text-amber-700 mt-2">Timeline: {p.timeline}</p>
                  )}
                  <p className="text-xs text-neutral-500 mt-2 line-clamp-2 min-h-[2.2em]">
                    {p.description}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
                    View plan <ArrowUpRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
